import { hasBlazeNow } from '@/stores/blaze';
import { effectiveVoiceSettings, useVoiceSettings, type VoiceSettings } from '@/stores/voiceSettings';

/* ------------------------------------------------------------------ */
/* Devices                                                             */
/* ------------------------------------------------------------------ */

export interface DeviceOption {
  deviceId: string;
  label: string;
}

export interface DeviceLists {
  inputs: DeviceOption[];
  outputs: DeviceOption[];
  cameras: DeviceOption[];
  /** False until the user has granted media permission (browsers hide labels before that). */
  labelled: boolean;
}

export const supportsOutputSelection = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

export async function listDevices(): Promise<DeviceLists> {
  if (!navigator.mediaDevices?.enumerateDevices) return { inputs: [], outputs: [], cameras: [], labelled: false };
  const devices = await navigator.mediaDevices.enumerateDevices();
  const pick = (kind: MediaDeviceKind, fallback: string) =>
    devices
      .filter((d) => d.kind === kind && d.deviceId !== 'communications')
      .map((d, i) => {
        const deviceId = d.deviceId || 'default';
        // Browsers hide device names until media permission is granted.
        return { deviceId, label: d.label || (deviceId === 'default' ? 'Default' : `${fallback} ${i + 1}`) };
      });
  return {
    inputs: pick('audioinput', 'Microphone'),
    outputs: pick('audiooutput', 'Speaker'),
    cameras: pick('videoinput', 'Camera'),
    labelled: devices.some((d) => d.label),
  };
}

/** Briefly opens the mic (and camera) so the browser reveals device names. */
export async function requestDeviceAccess(video = false): Promise<void> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video });
  stream.getTracks().forEach((t) => t.stop());
}

export function micConstraints(s: VoiceSettings): MediaTrackConstraints {
  const eff = effectiveVoiceSettings(s);
  return {
    deviceId: s.inputDeviceId && s.inputDeviceId !== 'default' ? { exact: s.inputDeviceId } : undefined,
    echoCancellation: eff.echoCancellation,
    noiseSuppression: eff.noiseSuppression,
    autoGainControl: eff.autoGainControl,
    channelCount: 1,
  };
}

export function cameraConstraints(s: VoiceSettings): MediaTrackConstraints {
  // 1080p with Blaze, 720p otherwise.
  const hd = hasBlazeNow();
  return {
    deviceId: s.videoDeviceId && s.videoDeviceId !== 'default' ? { exact: s.videoDeviceId } : undefined,
    width: { ideal: hd ? 1920 : 1280 },
    height: { ideal: hd ? 1080 : 720 },
    frameRate: { ideal: 30 },
  };
}

/** Routes an element to the chosen speaker where the browser supports it. */
export async function applyOutputDevice(el: HTMLMediaElement, deviceId: string) {
  if (!supportsOutputSelection) return;
  try {
    await (el as HTMLMediaElement & { setSinkId(id: string): Promise<void> }).setSinkId(deviceId === 'default' ? '' : deviceId);
  } catch {
    /* Device unplugged or permission missing — keep the current output. */
  }
}

/* ------------------------------------------------------------------ */
/* Push-to-talk key tracking                                           */
/* ------------------------------------------------------------------ */

type PttListener = (pressed: boolean) => void;
const pttListeners = new Set<PttListener>();
let pttPressed = false;
let pttInstalled = false;
/** Identifies a key for binding: the physical `code`, falling back to `key` for devices that omit it. */
export function keyIdOf(e: KeyboardEvent): string {
  return e.code || e.key;
}

/** Set while the settings UI is recording a new keybind, so the key press isn't treated as PTT. */
export const keybindRecording = { active: false };

function setPtt(pressed: boolean) {
  if (pttPressed === pressed) return;
  pttPressed = pressed;
  for (const listener of pttListeners) listener(pressed);
}

function installPtt() {
  if (pttInstalled) return;
  pttInstalled = true;
  window.addEventListener('keydown', (e) => {
    const { pushToTalk, pttKey } = useVoiceSettings.getState();
    if (!pushToTalk || !pttKey || keyIdOf(e) !== pttKey || e.repeat || keybindRecording.active) return;
    setPtt(true);
  });
  window.addEventListener('keyup', (e) => {
    if (keyIdOf(e) === useVoiceSettings.getState().pttKey) setPtt(false);
  });
  // A key released while the window is unfocused never fires keyup.
  window.addEventListener('blur', () => setPtt(false));
}

export function onPushToTalk(listener: PttListener): () => void {
  installPtt();
  pttListeners.add(listener);
  listener(pttPressed);
  return () => pttListeners.delete(listener);
}

/* ------------------------------------------------------------------ */
/* Microphone pipeline                                                 */
/* ------------------------------------------------------------------ */

export interface LevelReport {
  /** Input level in dBFS (after the volume stage, before the gate). */
  level: number;
  /** Current voice-activity threshold in dBFS. */
  threshold: number;
  /** Whether audio is currently being transmitted. */
  open: boolean;
  speaking: boolean;
}

/**
 * mic → volume → gate (AudioWorklet) → outgoing track.
 * The outgoing track is stable for the pipeline's lifetime, so switching the
 * microphone or its processing never requires renegotiating peer connections.
 */
export class MicPipeline {
  readonly ctx: AudioContext;
  private readonly inputGain: GainNode;
  private readonly destination: MediaStreamAudioDestinationNode;
  private gate: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private raw: MediaStream | null = null;
  private listeners = new Set<(r: LevelReport) => void>();
  private unsubscribers: Array<() => void> = [];
  private muted = false;
  private stopped = false;
  micAvailable = false;
  lastReport: LevelReport = { level: -100, threshold: -50, open: false, speaking: false };

  constructor() {
    this.ctx = new AudioContext();
    this.inputGain = this.ctx.createGain();
    this.destination = this.ctx.createMediaStreamDestination();
  }

  /** The processed, gated track to send to peers (or play back for a mic test). */
  get outputTrack(): MediaStreamTrack {
    return this.destination.stream.getAudioTracks()[0]!;
  }

  get outputStream(): MediaStream {
    return this.destination.stream;
  }

  /** Opens the microphone. Resolves `false` (listen-only) if the mic is unavailable. */
  async start(): Promise<boolean> {
    await this.ctx.resume();
    try {
      await this.ctx.audioWorklet.addModule('/audio/gate-processor.js');
      this.gate = new AudioWorkletNode(this.ctx, 'clanfire-gate', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        channelCount: 1,
        channelCountMode: 'explicit',
      });
      this.gate.port.onmessage = ({ data }: MessageEvent<LevelReport>) => {
        this.lastReport = data;
        for (const listener of this.listeners) listener(data);
      };
      this.inputGain.connect(this.gate).connect(this.destination);
    } catch {
      // No AudioWorklet support: transmit ungated rather than not at all.
      this.inputGain.connect(this.destination);
    }

    this.applySettings();
    this.unsubscribers.push(
      useVoiceSettings.subscribe((s, prev) => {
        const constraintsChanged =
          s.inputDeviceId !== prev.inputDeviceId ||
          JSON.stringify(effectiveVoiceSettings(s)) !== JSON.stringify(effectiveVoiceSettings(prev));
        this.applySettings();
        if (constraintsChanged) void this.openMic();
      }),
      onPushToTalk((pressed) => this.gate?.port.postMessage({ ptt: pressed })),
    );
    return this.openMic();
  }

  /** (Re)acquires the microphone with the current device and processing settings. */
  private async openMic(): Promise<boolean> {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: micConstraints(useVoiceSettings.getState()) });
    } catch {
      try {
        // The saved device may have been unplugged: fall back to the default mic.
        stream = await navigator.mediaDevices.getUserMedia({ audio: micConstraints({ ...useVoiceSettings.getState(), inputDeviceId: 'default' }) });
      } catch {
        this.micAvailable = false;
        return false;
      }
    }
    if (this.stopped) {
      stream.getTracks().forEach((t) => t.stop());
      return false;
    }
    this.source?.disconnect();
    this.raw?.getTracks().forEach((t) => t.stop());
    this.raw = stream;
    this.source = this.ctx.createMediaStreamSource(stream);
    this.source.connect(this.inputGain);
    this.micAvailable = true;
    return true;
  }

  private applySettings() {
    const s = useVoiceSettings.getState();
    this.inputGain.gain.setTargetAtTime(s.inputVolume / 100, this.ctx.currentTime, 0.02);
    const { gate } = effectiveVoiceSettings(s);
    this.gate?.port.postMessage({
      mode: gate.mode,
      thresholdDb: gate.mode === 'manual' ? gate.thresholdDb : undefined,
      pttReleaseMs: s.pttReleaseDelayMs,
      muted: this.muted,
    });
  }

  setMuted(muted: boolean) {
    this.muted = muted;
    this.gate?.port.postMessage({ muted });
  }

  subscribe(listener: (r: LevelReport) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  stop() {
    this.stopped = true;
    this.unsubscribers.forEach((u) => u());
    this.listeners.clear();
    this.source?.disconnect();
    this.raw?.getTracks().forEach((t) => t.stop());
    this.outputTrack.stop();
    void this.ctx.close().catch(() => undefined);
  }
}

/* ------------------------------------------------------------------ */
/* Level source for settings meters                                    */
/* ------------------------------------------------------------------ */

type PipelineProvider = () => MicPipeline | null;
const providers = new Set<PipelineProvider>();

/** Lets the settings UI read levels from whichever pipeline is live (mic test or a call). */
export function registerLevelProvider(provider: PipelineProvider): () => void {
  providers.add(provider);
  return () => providers.delete(provider);
}

export function activePipeline(): MicPipeline | null {
  for (const provider of [...providers].reverse()) {
    const pipeline = provider();
    if (pipeline) return pipeline;
  }
  return null;
}
